import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  useTeamsTeamMembersSalonMembersRetrieveQuery, 
  useSalonsMySalonsRetrieveQuery, 
  useServicesListQuery, 
  useTeamsTeamsListQuery,
  useAuthProfileRetrieveQuery
} from '../../../../services/GLOWClient';
import { PrimaryButton, TitleScreen } from '../../../../SystemDesign/styles';
import { 
  Button, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  Avatar, 
  IconButton, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Divider,
  Grid
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Person as PersonIcon, 
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import theme from '../../../../theme';
import AddMember from './addmember.jsx';
import UpdateMember from './updateMember';
import DeleteMember from './deleteMember';
import UpdateTeam from '../team/updateTeam';
import DeleteTeam from '../team/deleteTeam';

const TeamMember = ({
  salonId: propSalonId,
  createdTeams = [],
  onTeamDeleted,
  onManageUnavailability,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  
  // Team management state
  const [selectedTeam, setSelectedTeam] = React.useState(null);
  const [isUpdateTeamDialogOpen, setIsUpdateTeamDialogOpen] = React.useState(false);
  const [isDeleteTeamDialogOpen, setIsDeleteTeamDialogOpen] = React.useState(false);
  
  // Get user profile to check role
  const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
  const isEmployee = userProfile?.role === 'professional_employe';
  
  // Get the current salon ID - use prop if provided, otherwise fetch user's salons
  // Skip for employees as they don't manage salons
  const { data: salonsData } = useSalonsMySalonsRetrieveQuery({}, { skip: !!propSalonId || isEmployee });
  const [selectedSalonId, setSelectedSalonId] = React.useState(propSalonId || null);
  
  // Set the first salon as default when data is loaded (only if no prop provided)
  React.useEffect(() => {
    if (propSalonId) {
      setSelectedSalonId(propSalonId);
      return;
    }
    
    // Handle both array format and object with results property
    const salons = Array.isArray(salonsData) ? salonsData : salonsData?.results;
    if (salons && salons.length > 0 && !selectedSalonId) {
      setSelectedSalonId(salons[0].salon_id);
    }
  }, [salonsData, selectedSalonId, propSalonId]);

  // Fetch team members data for the selected salon using the API hook
  const { data: teamMembersData, error, isLoading, refetch } = useTeamsTeamMembersSalonMembersRetrieveQuery(
    selectedSalonId ? { salon: selectedSalonId } : undefined,
    { 
      skip: !selectedSalonId,
      refetchOnMountOrArgChange: true
    }
  );

  // Debug the query parameters being sent and trigger refetch when salon changes
  React.useEffect(() => {
    if (selectedSalonId) {
      refetch();
    }
  }, [selectedSalonId]);

  // Debug the query results
  React.useEffect(() => {
   
    // Check if data contains members from different salons
    if (teamMembersData && Array.isArray(teamMembersData)) {
      const salonIds = [...new Set(teamMembersData.map(member => String(member.salon_id)))];
     const expectedSalonIdStr = String(selectedSalonId);
      if (salonIds.length > 1 || (salonIds.length === 1 && salonIds[0] !== expectedSalonIdStr)) {
        console.warn('⚠️ Backend API issue: Data contains members from unexpected salons!');
        console.warn('⚠️ This indicates the backend is not properly filtering by salon ID');
     
      }
    }
  }, [teamMembersData, error, isLoading, selectedSalonId]);
  
  // Fetch services data
  const { data: servicesData, isLoading: servicesLoading, error: servicesError } = useServicesListQuery();
  

  // Fetch teams using the teams list query
  const { data: teamsRawData, isLoading: teamsLoading, refetch: refetchSalonTeams } = useTeamsTeamsListQuery(
    undefined,
    { skip: !selectedSalonId }
  );

  // Combine fetched teams with newly created ones from parent (immediate UI update)
  const combinedTeams = React.useMemo(() => {
    const teamsArray = Array.isArray(teamsRawData) 
      ? teamsRawData 
      : (teamsRawData?.results || []);
    
    const created = Array.isArray(createdTeams) ? createdTeams : [];

    // Normalize entries and dedupe by team_id (or id)
    const map = new Map();
    [...teamsArray, ...created].forEach(t => {
      const id = t.team_id || t.id;
      if (!id) return;
      // Prefer the created version to appear immediately if duplication
      map.set(id, { ...t, team_id: id });
    });

    // Filter by current salon if selected
    const list = Array.from(map.values());
    if (!selectedSalonId) return list;
    
    return list.filter(t => {
      const teamSalonId = typeof t.salon === 'object' ? t.salon.salon_id : t.salon;
      return String(teamSalonId) === String(selectedSalonId);
    });
  }, [teamsRawData, createdTeams, selectedSalonId]);
  
  // Transform and group the API response data by teams
  const getFallbackTeamName = React.useCallback((teamId) => {
    if (!teamId) {
      return t('pro_team_member.unassigned_team');
    }

    return t('pro_team_member.team_fallback_name', {
      id: teamId,
      defaultValue: `Team ${teamId}`
    });
  }, [t]);

  const transformAndGroupTeamMemberData = (data) => {
    if (!data) return {};
    
    const grouped = {};
    
    data.forEach((item) => {
      // Get service names from service_names field if available, otherwise fall back to mapping IDs
      let services_list = [];
      
      // Check if we have service_ids and servicesData available
      if (item.service_ids && Array.isArray(item.service_ids) && servicesData) {
        // Map service IDs to service names
        const serviceNames = item.service_ids
          .map(id => {
            const service = servicesData.find(s => s.service_id === id);
            return service ? service.name : null;
          })
          .filter(name => name); // Remove null names
        
        services_list = serviceNames;
      } 
      
      // Check for service_names as an alternative
      if (services_list.length === 0 && item.service_names && Array.isArray(item.service_names)) {
        services_list = item.service_names
          .map(service => {
            if (typeof service === 'object' && service.name) {
              return service.name;
            } else if (typeof service === 'string') {
              return service;
            }
            return null;
          })
          .filter(name => name); // Remove null names
      }
      
      const teamKey = item.team_name || getFallbackTeamName(item.team);
      
      if (!grouped[teamKey]) {
        grouped[teamKey] = {
          teamId: item.team,
          teamName: teamKey,
          members: []
        };
      }
      
      grouped[teamKey].members.push({
        id: item.team_member_id,
        team: item.team,
        team_name: teamKey,
        salon_id: item.salon_id,
        salon_name: item.salon_name || t('pro_team_member.unassigned_team'),
        user: item.user,
        user_name: item.user_details?.full_name || item.user_details?.username || 'N/A',
        email: item.user_details?.email || '',
        phone: item.user_details?.phone || '',
        has_access: item.user_details?.has_access,
        first_name: item.user_details?.first_name || '',
        last_name: item.user_details?.last_name || '',
        service_ids: item.service_ids || [],
        services_list: services_list,
        raw: item, // Store the original item for use in handlers
      });
    });
    
    return grouped;
  };

  const groupedData = React.useMemo(() => {
    if (!teamMembersData) return {};
    
    // Filter to only include members from the selected salon as a safety measure
    const filteredData = teamMembersData.filter(member => {
      const memberSalonId = String(member.salon_id); // Convert to string for comparison
      const selectedSalonIdStr = String(selectedSalonId); // Convert to string for comparison
      const matches = memberSalonId === selectedSalonIdStr;
      
      if (!matches) {
        console.warn(`Filtering out member ${member.user_details?.full_name} from salon ${memberSalonId} (expected ${selectedSalonIdStr})`);
      }
      
      return matches;
    });
    
    return transformAndGroupTeamMemberData(filteredData);
  }, [teamMembersData, servicesData, selectedSalonId, getFallbackTeamName, i18n.language]);

  // Merge in empty teams so they appear even with 0 members
  const groupedDataWithEmptyTeams = React.useMemo(() => {
    const base = { ...groupedData };
    const teamsForSalon = combinedTeams;

    teamsForSalon.forEach(team => {
      const teamId = team.team_id || team.id;
      const exists = Object.values(base).some(group => group.teamId === teamId);
      if (!exists) {
        const teamName = team.name || getFallbackTeamName(teamId);
        base[teamName] = {
          teamId,
          teamName,
          members: []
        };
      }
    });

    return base;
  }, [groupedData, combinedTeams, getFallbackTeamName]);

  const handleView = (member) => {
    // View functionality can be implemented here
  };

  const handleUpdateClick = (member) => {
    setSelectedMember(member.raw);
    setIsUpdateDialogOpen(true);
  };

  const handleDeleteClick = (member) => {
    setSelectedMember(member.raw);
    setIsDeleteDialogOpen(true);
  };
  
  const handleEditTeam = (teamData) => {
    const teamToEdit = {
      ...teamData,
      id: teamData.teamId,
      team_id: teamData.teamId,
      name: teamData.teamName
    };
    setSelectedTeam(teamToEdit);
    setIsUpdateTeamDialogOpen(true);
  };
  
  const handleDeleteTeam = (teamData) => {
    const teamToDelete = {
      ...teamData,
      id: teamData.teamId,
      name: teamData.teamName
    };
    setSelectedTeam(teamToDelete);
    setIsDeleteTeamDialogOpen(true);
  };

  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };
  
  const handleDialogClose = () => {
    setIsAddDialogOpen(false);
    setIsUpdateDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setSelectedMember(null);
    // Only refetch if we have a selected salon and the query is not skipped
    if (selectedSalonId) {
      refetch(); // Refresh the data after dialog closes
    }
  };
  
  const handleTeamDialogClose = () => {
    setIsUpdateTeamDialogOpen(false);
    setIsDeleteTeamDialogOpen(false);
    setSelectedTeam(null);
    // Refetch data after team operations
    if (selectedSalonId) {
      refetch();
    }
  };

  return (
    <>
      <div style={{ padding: 0 }}>
        {/* Header Section with proper spacing */}
        <div style={{ marginBottom: '16px' }}>
          <Typography sx={{
            ...TitleScreen(theme.colors),
            fontSize: '1rem',
            lineHeight: '1.2',
            margin: 0,
            marginBottom: '8px'
          }}>
            {t('pro_team_member.group_member_management')}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleAddClick}
              style={{
                ...PrimaryButton,
                backgroundColor: theme.colors.secondary[500],
                fontSize: '14px',
                padding: '8px 20px',
                height: '40px',
                borderRadius: '20px',
                whiteSpace: 'nowrap',
                minWidth: '140px',
                textTransform: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontWeight: '500',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {t('pro_team_member.create_member')}
            </Button>
          </Box>
        </div>

        {/* Content Section with proper spacing */}
        <div style={{ marginTop: 0 }}>
          {isLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '60px 0',
              color: theme.colors.text.secondary
            }}>
              {t('common.loading')}
            </div>
          ) : error ? (
            <div style={{ 
              padding: '20px',
              backgroundColor: theme.colors.error?.light || '#ffebee',
              color: theme.colors.error?.main || '#d32f2f',
              borderRadius: '8px',
              border: `1px solid ${theme.colors.error?.main || '#d32f2f'}`,
              marginBottom: '24px'
            }}>
              {t('common.error_try_again')}: {error.message}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {Object.keys(groupedDataWithEmptyTeams).length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center',
                  padding: '40px 20px'
                }}>
                  <GroupIcon sx={{ fontSize: 48, color: theme.colors.text.secondary, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
                    {t('pro_team_member.no_member_found')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                    {t('pro_team_member.create_first_member')}
                  </Typography>
                </Box>
              ) : (
                Object.entries(groupedDataWithEmptyTeams).map(([teamName, teamData]) => (
                  <Accordion 
                    key={teamName}
                    defaultExpanded
                    sx={{ 
                      backgroundColor: 'transparent',
                      borderRadius: 0,
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      mb: 0
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        minHeight: '48px',
                        px: 0,
                        '&.Mui-expanded': {
                          minHeight: '48px',
                          borderRadius: 0
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <GroupIcon sx={{ color: theme.colors.primary[600] }} />
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            color: theme.colors.primary[700],
                            flex: 1
                          }}
                        >
                          {teamName}
                        </Typography>
                        <Chip 
                          label={t('pro_team_member.members_badge', { count: teamData.members.length })}
                          size="small"
                          sx={{
                            backgroundColor: theme.colors.primary[100],
                            color: theme.colors.primary[700],
                            fontWeight: 500
                          }}
                        />
                        
                        {/* Team Edit and Delete Icons */}
                        {teamData.teamId ? (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTeam(teamData);
                              }}
                              size="small"
                              sx={{
                                color: theme.colors.primary[600],
                                '&:hover': {
                                  backgroundColor: theme.colors.primary[50]
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTeam(teamData);
                              }}
                              size="small"
                              sx={{
                                color: theme.colors.error?.main || '#d32f2f',
                                '&:hover': {
                                  backgroundColor: theme.colors.error?.light || '#ffebee'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : null}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0 }}>
                      <Grid container spacing={2} justifyContent="center" sx={{ px: 2, pb: 2 }}>
                        {teamData.members.map((member, index) => (
                          <Grid item xs={12} sm={6} md={6} lg={4} key={member.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Card 
                              sx={{ 
                                height: '100%',
                                width: { xs: '100%', sm: 520 },
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '12px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderTop: `1px solid ${theme.colors.grey[200]}`,
                                borderBottom: `1px solid ${theme.colors.grey[200]}`,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  transform: 'translateY(-2px)'
                                },
                                m: 0,
                                maxWidth: '100%'
                              }}
                            >
                              <CardContent sx={{ padding: '16px', paddingBottom: '16px', flex: 1, '&:last-child': { paddingBottom: '16px' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                  <Avatar 
                                    sx={{ 
                                      backgroundColor: theme.colors.secondary[100],
                                      color: theme.colors.secondary[700],
                                      width: 48,
                                      height: 48
                                    }}
                                  >
                                    <PersonIcon />
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* First line: Name + Phone */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                      <Typography 
                                        variant="h6" 
                                        sx={{ 
                                          fontWeight: 600,
                                          fontSize: '16px',
                                          color: theme.colors.text.primary,
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {member.user_name}
                                      </Typography>
                                      {member.has_access === false && (
                                        <Chip
                                          label={t('pro_team_member.add_member.without_access')}
                                          size="small"
                                          sx={{
                                            backgroundColor: theme.colors.grey[100],
                                            color: theme.colors.text.secondary,
                                            fontSize: '11px',
                                            height: '22px'
                                          }}
                                        />
                                      )}
                                      {member.phone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <PhoneIcon sx={{ fontSize: 14, color: theme.colors.text.secondary }} />
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              color: theme.colors.text.secondary,
                                              fontSize: '12px'
                                            }}
                                          >
                                            {member.phone}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                    
                                    {/* Second line: Email */}
                                    {member.email && member.has_access !== false && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 14, color: theme.colors.text.secondary }} />
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: theme.colors.text.secondary,
                                            fontSize: '12px',
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {member.email}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                                
                                <Box sx={{ minHeight: 96 }}>
                                  <Divider sx={{ my: 2 }} />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 600,
                                      color: theme.colors.text.primary,
                                      mb: 1
                                    }}
                                  >
                                    {t('pro_team_member.add_member.services')}
                                  </Typography>
                                  {member.services_list.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 56, overflow: 'hidden' }}>
                                      {member.services_list.slice(0, 6).map((service, serviceIndex) => (
                                        <Chip
                                          key={serviceIndex}
                                          label={service}
                                          size="small"
                                          sx={{
                                            backgroundColor: theme.colors.secondary[50],
                                            color: theme.colors.secondary[700],
                                            fontSize: '11px',
                                            height: '24px'
                                          }}
                                        />
                                      ))}
                                      {member.services_list.length > 6 && (
                                        <Chip
                                          label={`+${member.services_list.length - 6}`}
                                          size="small"
                                          sx={{
                                            backgroundColor: theme.colors.grey[100],
                                            color: theme.colors.text.secondary,
                                            fontSize: '11px',
                                            height: '24px'
                                          }}
                                        />
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: theme.colors.text.secondary,
                                        fontSize: '12px'
                                      }}
                                    >
                                      {t('pro_team_member.add_member.no_services_available')}
                                    </Typography>
                                  )}
                                </Box>
                              </CardContent>
                              <CardActions sx={{ padding: '16px 20px', paddingTop: 0, justifyContent: 'flex-end' }}>
                                {onManageUnavailability && (
                                  <IconButton
                                    onClick={() => onManageUnavailability(member)}
                                    size="small"
                                    title="Ajouter un congé ou une absence"
                                    sx={{
                                      color: theme.colors.warning?.[700] || '#a15c21',
                                      '&:hover': {
                                        backgroundColor: theme.colors.warning?.[50] || '#fff7ed'
                                      }
                                    }}
                                  >
                                    <EventBusyIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton
                                  onClick={() => handleUpdateClick(member)}
                                  size="small"
                                  sx={{
                                    color: theme.colors.primary[600],
                                    '&:hover': {
                                      backgroundColor: theme.colors.primary[50]
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeleteClick(member)}
                                  size="small"
                                  sx={{
                                    color: theme.colors.error?.main || '#d32f2f',
                                    '&:hover': {
                                      backgroundColor: theme.colors.error?.light || '#ffebee'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Team Member Management Dialogs */}
      {isAddDialogOpen && (
        <AddMember 
          open={isAddDialogOpen} 
          onClose={handleDialogClose}
          salonId={selectedSalonId}
        />
      )}
      
      {isUpdateDialogOpen && selectedMember && (
        <UpdateMember 
          open={isUpdateDialogOpen} 
          onClose={handleDialogClose} 
          member={{...selectedMember, available_teams: combinedTeams}}
          salons={Array.isArray(salonsData) ? salonsData : salonsData?.results}
        />
      )}
      
      {isDeleteDialogOpen && selectedMember && (
        <DeleteMember 
          open={isDeleteDialogOpen} 
          onClose={handleDialogClose} 
          member={selectedMember} 
        />
      )}
      
      {/* Team Management Dialogs */}
      <UpdateTeam
        open={isUpdateTeamDialogOpen && selectedTeam !== null}
        onClose={handleTeamDialogClose}
        onSuccess={() => {
          handleTeamDialogClose();
          // You can add a success message here if needed
        }}
        team={selectedTeam}
      />
      
      {isDeleteTeamDialogOpen && selectedTeam && (
        <DeleteTeam
          open={isDeleteTeamDialogOpen && selectedTeam !== null}
          onClose={handleTeamDialogClose}
          onSuccess={() => {
            // notify parent to sync local createdTeams and refetch teams from server
            if (onTeamDeleted) {
              onTeamDeleted(selectedTeam);
            }
            refetchSalonTeams && refetchSalonTeams();
            handleTeamDialogClose();
          }}
          team={selectedTeam}
        />
      )}
    </>
  );
};

export default TeamMember;
